import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import {
  PartnerEmptyState,
  PartnerLoading,
  PartnerPageShell,
  partnerOxbloodBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';

/**
 * Partner-rail: own Private Travel referrals only (no DVN economics).
 */
const PartnerPrivateReferrals = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [referrals, setReferrals] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/partner/private-travel/referrals');
      setReferrals(data.referrals || []);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerPrivate.loadError'));
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PartnerPageShell error={error} onClearError={() => setError('')}>
      <Box sx={{ pt: { xs: 3, md: 4 }, pb: 2 }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 28, mb: 0.5 }}>
          {t('partnerPrivate.title')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14, mb: 3, maxWidth: 520 }}>
          {t('partnerPrivate.subtitle')}
        </Typography>
        {loading ? (
          <PartnerLoading />
        ) : referrals.length === 0 ? (
          <PartnerEmptyState
            title={t('partnerPrivate.emptyTitle')}
            body={t('partnerPrivate.emptyBody')}
            action={(
              <Button sx={partnerOxbloodBtnSx} onClick={load}>
                {t('partnerPrivate.refresh')}
              </Button>
            )}
          />
        ) : (
          <TableContainer sx={{ border: `1px solid ${DOCUMENT.ruleHard}`, bgcolor: DOCUMENT.surface }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('partnerPrivate.cols.client')}</TableCell>
                  <TableCell>{t('partnerPrivate.cols.stage')}</TableCell>
                  <TableCell>{t('partnerPrivate.cols.ownership')}</TableCell>
                  <TableCell>{t('partnerPrivate.cols.source')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referrals.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.clientName || '—'}</TableCell>
                    <TableCell>{row.inquiryStage || '—'}</TableCell>
                    <TableCell>{row.ownershipType}</TableCell>
                    <TableCell>{row.campaignSource || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && error ? (
          <Alert severity="info" sx={{ mt: 2, borderRadius: 0 }}>{error}</Alert>
        ) : null}
      </Box>
    </PartnerPageShell>
  );
};

export default PartnerPrivateReferrals;
